import React, {ReactElement, useContext, useEffect, useState} from 'react';
import {User, UserContext} from '../../../../context/userContext';
import {useWorkspaceContext} from '../../../../context/workspaceContext';
import {uuid} from '../../../../utils/utils';
import {ResourceType} from '../../../../@types/resource-type';
import UserService from '../../../../services/users.service';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormGroup from '@mui/material/FormGroup';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import Stack from '@mui/material/Stack';
import ErrorAlertComponent from '../../../../components/ErrorAlert.component';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import {UserEditorProps} from '../../../../@types/editor-props';
import {toast} from 'react-toastify';

export default function UserEditor(props: UserEditorProps): ReactElement {
    const { user, updateTab } = props;
    const {setCurrentWorkspaceObject, publishResourceUpdate } = useWorkspaceContext();
    const { user: me, signinUser} = useContext(UserContext);

    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [username, setUsername] = useState<string>();
    const [isActive, setIsActive] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [editMode, setEditMode] = useState(false);
    const [isRunning, setIsRunning] = useState<boolean>(false);
    const uuidRef = React.useRef(uuid());

    const onFirstNameChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        setFirstName(event.target.value);
    };

    const onLastNameChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        setLastName(event.target.value);
    };

    const onUsernameChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        setUsername(event.target.value);
    };

    const formUser = (): User => {
        const userData = {} as User;
        userData.firstName = firstName ?? '';
        userData.lastName = lastName ?? '';
        userData.username = username ?? '';

        if (user && user.id) {
            userData.id = user.id;
            userData.isActive = isActive;
        }
        return userData;
    }

    const updateUser = (usr: User): void => {
        let userObject = { objectType: ResourceType.USERS, object: usr };
        setCurrentWorkspaceObject(userObject);
        updateTab(userObject);
        // Publish update event for other components
        publishResourceUpdate({
            type: ResourceType.USERS,
            id: usr.id,
            updatedData: usr,
            source: 'save'
        });

    }

    const onClickSave = (_event: React.SyntheticEvent): void => {
        if (firstName && lastName && username) {
            setError(null);
            if (editMode) {
                setIsRunning(true);
                UserService.patchUser(formUser()).then((usr: User) => {
                    updateUser(usr);
                    if (me?.id === usr.id) {
                        signinUser(usr);
                    }
                    toast.success('User Profile updated successfully');
                }).catch((error: Error) => {
                    setError(error.message);
                }).finally(() => {
                    setIsRunning(false);
                });
            } else {
                setIsRunning(true);
                UserService.createUser(formUser()).then((usr: User) => {
                    updateUser(usr);
                    setEditMode(true);
                    toast.success('User Profile created successfully');
                }).catch((error: Error) => {
                    setError(error.message);
                }).finally(() => {
                    setIsRunning(false);
                });
            }
                
        } else {
            setError('Please fill in all fields');
        }
    }
    useEffect(() => {
        setIsRunning(false);
        if (user) {
            setFirstName(user.firstName);
            setLastName(user.lastName);
            setUsername(user.username);
            setIsActive(user.isActive);
            if (user.id) {
                setEditMode(true);
            }
        }
    }, [user]);

    return (
        <FormGroup className='formStyle' sx={{ p: 2 }}>
            <Stack className='formControl'>
                <Typography variant='h6' gutterBottom>{editMode ? 'Edit User' : 'New User'}</Typography>
                <Stack direction='row' spacing={2} justifyContent='space-between'>
                    {me && me.superUser &&
                    <Typography variant='body1' gutterBottom>
                        You can manage the groups of the user
                        and indicate whether or not the user
                        should be a super user.
                    </Typography>
                    }
                

                    <Stack className='formControl'>
                        <TextField
                            id={`first-name-input-${uuidRef.current}`}
                            label='First Name'
                            aria-label='user first name'
                            type='text'   
                            variant='outlined'
                            size='small'
                            autoComplete='on'
                            value={firstName || ''}
                            onChange={onFirstNameChange}
                            sx={{
                                '& input': {
                                    border: 'none',
                                }
                            }}
                            slotProps={{
                                input: {
                                    'role': 'textbox',
                                    'tabIndex': 0,
                                    'aria-labelledby': `first-name-input-${uuidRef.current}`,
                                    'aria-label': 'user first name'
                                }
                            }}
                        />
                        <hr />
                        <TextField
                            id={`last-name-input-${uuidRef.current}`}
                            label='Last Name'
                            aria-label='user last name'
                            type='text'   
                            variant='outlined'
                            size='small'
                            autoComplete='on'
                            value={lastName || ''}
                            onChange={onLastNameChange}
                            sx={{
                                '& input': {
                                    border: 'none',
                                }
                            }}
                            slotProps={{
                                input: {
                                    'role': 'textbox',
                                    'tabIndex': 0,
                                    'aria-labelledby': `last-name-input-${uuidRef.current}`,
                                    'aria-label': 'user last name'
                                }
                            }}
                        />
                        <hr />
                        <TextField
                            id={`username-address-input-${uuidRef.current}`}
                            label='Email Address'
                            aria-label='user username address'
                            type='text'   
                            variant='outlined'
                            size='small'
                            autoComplete='on'
                            value={username || ''}
                            onChange={onUsernameChange}
                            sx={{
                                '& input': {
                                    border: 'none',
                                }
                            }}
                            slotProps={{
                                input: {
                                    'role': 'textbox',
                                    'tabIndex': 0,
                                    'aria-labelledby': `username-address-input-${uuidRef.current}`,
                                    'aria-label': 'user username address'
                                }
                            }}
                        />
                        <hr />
                        
                        {user && user.id && user.id !== me?.id &&
                        <FormControl>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox checked={isActive} onChange={():void => setIsActive(!isActive)} />
                                    }
                                    label='Active'
                                    sx={{ ml: 1 }}
                                />
                            </FormGroup>
                        </FormControl>
                        
                        }

                        
                        <div>
                            <Button className='formBtn' id={`save-user-${uuidRef.current}`}
                                size='small' variant='contained'
                                aria-label='save user'
                                title='Save User'
                                onClick={onClickSave}
                                startIcon={isRunning ? <CircularProgress size={20} /> : <SaveAltIcon />}
                                disabled={isRunning || (!me?.superUser && me?.id !== user?.id)}
                            >Save</Button>
                        </div>                   
                        
                        {error ? <ErrorAlertComponent message={error} />  : null}
                    </Stack>
                </Stack>
            </Stack>
        </FormGroup>
    );
}
